import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import {
  MapPinIcon,
  GlobeAltIcon,
  StarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { SpotManagementService, SpotInfo, NearbySpot } from "@/services/SpotManagementService";
import { LiveService } from "@/services/LiveService";
import { useAuth } from "@/context/AuthContext";
import "leaflet/dist/leaflet.css";

interface SpotInfoPanelProps {
  spotId: number;
  spotName: string;
}

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom marker icon for the main spot
const createMainSpotIcon = () => {
  return L.divIcon({
    className: "main-spot-marker",
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <svg viewBox="0 0 24 32" width="32" height="40" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
          <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="#3b82f6"/>
          <circle cx="12" cy="11" r="5" fill="white"/>
        </svg>
      </div>
    `,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
  });
};

// Custom marker icon for nearby spots
const createNearbySpotIcon = () => {
  return L.divIcon({
    className: "nearby-spot-marker",
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <svg viewBox="0 0 24 32" width="20" height="26" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));">
          <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="#ef4444"/>
          <circle cx="12" cy="11" r="4" fill="white"/>
        </svg>
      </div>
    `,
    iconSize: [20, 26],
    iconAnchor: [10, 26],
    popupAnchor: [0, -26],
  });
};

// Component to fit map bounds
const MapBoundsUpdater: React.FC<{ spots: Array<{ latitude: number; longitude: number }> }> = ({ spots }) => {
  const map = useMap();

  useEffect(() => {
    if (spots.length > 0) {
      const bounds = L.latLngBounds(spots.map(s => [s.latitude, s.longitude]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
    }
  }, [spots, map]);

  return null;
};

const SpotInfoPanel: React.FC<SpotInfoPanelProps> = ({ spotId, spotName }) => {
  const { user } = useAuth();
  const [spotInfo, setSpotInfo] = useState<SpotInfo | null>(null);
  const [nearbySpots, setNearbySpots] = useState<NearbySpot[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSpotData = async () => {
      if (!user?.accesstoken) return;

      setIsLoading(true);
      try {
        // Load spot info
        const info = await SpotManagementService.getSpotInfo(spotId, user.accesstoken);
        setSpotInfo(info);

        // Load nearby spots after we have coordinates
        if (info?.latitude && info?.longitude) {
          const nearbyResults = await SpotManagementService.getNearbySpots(
            info.latitude,
            info.longitude,
            spotId,
            25
          );
          setNearbySpots(nearbyResults.slice(0, 10)); // Limit to 10 nearby spots
        }
      } catch (error) {
        console.error("Error loading spot data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSpotData();
  }, [spotId, user?.accesstoken]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!spotInfo) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500 text-center">Unable to load spot information</p>
      </div>
    );
  }

  const allSpotsForMap = [
    { latitude: spotInfo.latitude, longitude: spotInfo.longitude },
    ...nearbySpots,
  ];

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Map Section */}
      <div className="h-48 relative">
        <MapContainer
          center={[spotInfo.latitude, spotInfo.longitude]}
          zoom={10}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapBoundsUpdater spots={allSpotsForMap} />

          {/* Main spot marker */}
          <Marker
            position={[spotInfo.latitude, spotInfo.longitude]}
            icon={createMainSpotIcon()}
          >
            <Popup>
              <div className="font-semibold">{spotInfo.name}</div>
              <div className="text-xs text-gray-500">
                {spotInfo.latitude.toFixed(4)}, {spotInfo.longitude.toFixed(4)}
              </div>
            </Popup>
          </Marker>

          {/* Nearby spot markers */}
          {nearbySpots.map((spot) => (
            <Marker
              key={spot.id}
              position={[spot.latitude, spot.longitude]}
              icon={createNearbySpotIcon()}
            >
              <Popup>
                <div className="font-semibold">{spot.name}</div>
                <div className="text-xs text-gray-500">
                  {spot.distance.toFixed(1)} km away
                </div>
                {spot.sessionCount !== undefined && spot.sessionCount > 0 && (
                  <div className="text-xs text-gray-500">
                    {spot.sessionCount} sessions
                  </div>
                )}
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Map overlay with spot count */}
        {nearbySpots.length > 0 && (
          <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
            {nearbySpots.length} nearby spots
          </div>
        )}
      </div>

      {/* Spot Details */}
      <div className="p-4">
        {/* Location Info */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
          <MapPinIcon className="h-4 w-4" />
          <span>
            {spotInfo.latitude.toFixed(4)}, {spotInfo.longitude.toFixed(4)}
          </span>
          {spotInfo.countryName && (
            <>
              <span className="text-gray-300">|</span>
              <GlobeAltIcon className="h-4 w-4" />
              <span>{spotInfo.countryName}</span>
              {spotInfo.alpha2 && (
                <img
                  src={`https://flagcdn.com/16x12/${spotInfo.alpha2.toLowerCase()}.png`}
                  alt={spotInfo.alpha2}
                  className="h-3"
                />
              )}
            </>
          )}
          {spotInfo.timezoneName && (
            <>
              <span className="text-gray-300">|</span>
              <ClockIcon className="h-4 w-4" />
              <span>{spotInfo.timezoneName}</span>
            </>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Rating */}
          {spotInfo.meanreviews !== undefined && spotInfo.nreviews !== undefined && spotInfo.nreviews > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-yellow-500 mb-1">
                <StarIcon className="h-4 w-4 fill-current" />
                <span className="font-semibold">{spotInfo.meanreviews.toFixed(1)}</span>
              </div>
              <div className="text-xs text-gray-500">{spotInfo.nreviews} reviews</div>
            </div>
          )}

          {/* Total Sessions */}
          {spotInfo.totalsessions !== undefined && spotInfo.totalsessions > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-blue-500 mb-1">
                <ChartBarIcon className="h-4 w-4" />
                <span className="font-semibold">{spotInfo.totalsessions.toLocaleString()}</span>
              </div>
              <div className="text-xs text-gray-500">total sessions</div>
            </div>
          )}

          {/* Total Riders */}
          {spotInfo.nriders !== undefined && spotInfo.nriders > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-green-500 mb-1">
                <UserGroupIcon className="h-4 w-4" />
                <span className="font-semibold">{spotInfo.nriders.toLocaleString()}</span>
              </div>
              <div className="text-xs text-gray-500">riders</div>
            </div>
          )}

          {/* Max Height */}
          {spotInfo.maxheight !== undefined && spotInfo.maxheight > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-purple-500 mb-1">
                <ArrowTrendingUpIcon className="h-4 w-4" />
                <span className="font-semibold">{spotInfo.maxheight.toFixed(1)}m</span>
              </div>
              <div className="text-xs text-gray-500">max height</div>
            </div>
          )}

          {/* Max Airtime */}
          {spotInfo.maxairtime !== undefined && spotInfo.maxairtime > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-orange-500 mb-1">
                <ClockIcon className="h-4 w-4" />
                <span className="font-semibold">{spotInfo.maxairtime.toFixed(1)}s</span>
              </div>
              <div className="text-xs text-gray-500">max airtime</div>
            </div>
          )}

          {/* Max Distance */}
          {spotInfo.maxdistance !== undefined && spotInfo.maxdistance > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1 text-teal-500 mb-1">
                <MapPinIcon className="h-4 w-4" />
                <span className="font-semibold">{spotInfo.maxdistance.toFixed(0)}m</span>
              </div>
              <div className="text-xs text-gray-500">max distance</div>
            </div>
          )}

          {/* Live Riders */}
          {spotInfo.riderslive !== undefined && spotInfo.riderslive > 0 && (
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <div className="flex items-center gap-1 text-red-500 mb-1">
                <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></div>
                <span className="font-semibold">{spotInfo.riderslive}</span>
              </div>
              <div className="text-xs text-red-600">live now</div>
            </div>
          )}
        </div>

        {/* Windguru Link */}
        {spotInfo.wgspotid && (
          <div className="mt-4 pt-4 border-t">
            <a
              href={`https://www.windguru.cz/station/${spotInfo.wgspotid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1"
            >
              <GlobeAltIcon className="h-4 w-4" />
              View on Windguru
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpotInfoPanel;
